import { useActiveTenantEditable } from './useActiveTenantEditable';

export interface PublishGateResult {
  /** True when publish cannot proceed right now (read-only env). */
  disabled: boolean;
  /** Short human-readable reason — used as the button tooltip. */
  reason: string;
}

/**
 * Whether publishing the active tenant can proceed right now — drives the
 * top-bar Publish button's color/tooltip (the button stays CLICKABLE so the
 * user can still open the Publish page; the real gate is the server fence +
 * the Publish page).
 *
 * Mirrors PublishTab's own publish gate so the two never disagree:
 *   - tenant read-only HERE (non-home / read-only env) → can't publish at all.
 *
 * Content readiness (Storyblok-only per-cohort content coverage) is ADVISORY
 * only — the Publish page shows a warning panel with the gaps, but neither
 * this hook nor the server blocks publish on it. (It used to; the strict rule
 * mis-fires on auto-created adventure wiring cohorts, whose content lives in
 * CMS components rather than per-cohort content variants.)
 */
export function usePublishGate(tenantCode: string | undefined): PublishGateResult {
  const { editable, homeEnv } = useActiveTenantEditable();

  if (!tenantCode) return { disabled: true, reason: 'Select a tenant to publish' };
  if (!editable) {
    return {
      disabled: true,
      reason: `Read-only here — author on ${homeEnv} to publish`,
    };
  }
  return { disabled: false, reason: 'Publish to platform' };
}

export default usePublishGate;
